import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Drawer, Form, Input, InputNumber, Switch, Space, Tag, message,
  Popconfirm, Select, Radio, Divider, Alert, TimePicker, Typography, Spin, Tooltip
} from 'antd';
import { PlusOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Text } = Typography;

const ORDER_BY_OPTIONS = [
  { value: 'status_changed_asc', label: '状态停留最久优先（推荐）' },
  { value: 'id_desc', label: '订单号倒序' },
  { value: 'id_asc', label: '订单号正序' }
];

function scheduleText(row) {
  if (row.schedule_type === 'daily') {
    return row.daily_times ? `每天 ${String(row.daily_times).split(',').join('、')}` : '每天（未设置时刻）';
  }
  return `每 ${row.interval_minutes} 分钟`;
}

function paramsText(row, meta) {
  const p = row.params || {};
  const statusMap = {};
  (meta.statuses || []).forEach(s => { statusMap[s.id] = s.name; });
  const supplierMap = {};
  (meta.suppliers || []).forEach(s => { supplierMap[s.id] = s.name; });

  const statuses = (p.status_ids || []).length
    ? p.status_ids.map(id => statusMap[id] || `#${id}`).join('、')
    : '涨粉中';
  const suppliers = (p.supplier_ids || []).length
    ? p.supplier_ids.map(id => supplierMap[id] || `#${id}`).join('、')
    : '全部主体';
  return { statuses, suppliers, max: p.max_orders, cooldown: p.cooldown_minutes };
}

export default function ScheduledTasks() {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ types: [], statuses: [], suppliers: [], admins: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '', enabled: '' });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [scheduleType, setScheduleType] = useState('interval');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const res = await api.get('/scheduled-tasks/meta');
      if (res.code === 200) setMeta(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.name) params.name = filters.name;
      if (filters.enabled !== '') params.enabled = filters.enabled;
      const res = await api.get('/scheduled-tasks', { params });
      if (res.code === 200) setList(res.data.list || []);
    } catch (e) {
      message.error('加载定时任务失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadList(); }, [loadList]);

  const openCreate = () => {
    setEditing(null);
    setPreview(null);
    setScheduleType('interval');
    const defaultStatus = (meta.statuses || []).find(s => s.name === '涨粉中');
    form.setFieldsValue({
      name: '',
      type: 'fan_pull',
      enabled: true,
      schedule_type: 'interval',
      interval_minutes: 60,
      daily_times: ['09:00'],
      window_start: dayjs('08:00', 'HH:mm'),
      window_end: dayjs('23:00', 'HH:mm'),
      status_ids: defaultStatus ? [defaultStatus.id] : [],
      supplier_ids: [],
      max_orders: 200,
      cooldown_minutes: 720,
      order_by: 'status_changed_asc',
      run_as_user_id: meta.current_user_id,
      fail_stop_after: 3
    });
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setPreview(null);
    setScheduleType(row.schedule_type);
    const p = row.params || {};
    form.setFieldsValue({
      name: row.name,
      type: row.type,
      enabled: !!row.enabled,
      schedule_type: row.schedule_type,
      interval_minutes: row.interval_minutes || 60,
      daily_times: (row.daily_times ? String(row.daily_times).split(',').filter(Boolean) : ['09:00']),
      window_start: dayjs(String(row.window_start || '08:00:00').slice(0, 5), 'HH:mm'),
      window_end: dayjs(String(row.window_end || '23:00:00').slice(0, 5), 'HH:mm'),
      status_ids: p.status_ids || [],
      supplier_ids: p.supplier_ids || [],
      max_orders: p.max_orders || 200,
      cooldown_minutes: p.cooldown_minutes === undefined ? 720 : p.cooldown_minutes,
      order_by: p.order_by || 'status_changed_asc',
      run_as_user_id: row.run_as_user_id || row.created_by,
      fail_stop_after: row.fail_stop_after === undefined ? 3 : row.fail_stop_after
    });
    setDrawerOpen(true);
  };

  const buildPayload = (values) => ({
    name: values.name,
    type: values.type,
    enabled: values.enabled ? 1 : 0,
    schedule_type: values.schedule_type,
    interval_minutes: values.interval_minutes,
    daily_times: (values.daily_times || []).filter(Boolean),
    window_start: values.window_start ? values.window_start.format('HH:mm') : '08:00',
    window_end: values.window_end ? values.window_end.format('HH:mm') : '23:00',
    fail_stop_after: values.fail_stop_after,
    run_as_user_id: values.run_as_user_id || null,
    params: {
      status_ids: values.status_ids || [],
      supplier_ids: values.supplier_ids || [],
      max_orders: values.max_orders,
      cooldown_minutes: values.cooldown_minutes,
      order_by: values.order_by
    }
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = buildPayload(values);
      const res = editing
        ? await api.put(`/scheduled-tasks/${editing.id}`, payload)
        : await api.post('/scheduled-tasks', payload);
      if (res.code === 200) {
        message.success(editing ? '保存成功' : '创建成功');
        setDrawerOpen(false);
        loadList();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (e?.errorFields) { /* 表单校验失败 */ } else message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row, checked) => {
    try {
      const res = await api.put(`/scheduled-tasks/${row.id}/enabled`, { enabled: checked ? 1 : 0 });
      if (res.code === 200) { message.success(checked ? '已启用' : '已停用'); loadList(); }
    } catch (e) {
      message.error('操作失败');
    }
  };

  const runNow = async (row) => {
    try {
      const pv = await api.get(`/scheduled-tasks/${row.id}/preview`);
      const count = pv.code === 200 ? pv.data.count : 0;
      if (!count) return message.warning('按当前规则没有待拉取的抖音号');
      const hide = message.loading(`正在创建任务（预计 ${count} 个抖音号）…`, 0);
      try {
        const res = await api.post(`/scheduled-tasks/${row.id}/run`);
        if (res.code === 200) {
          message.success(`任务已创建：${res.data.total} 个抖音号`);
          loadList();
        } else {
          message.error(res.message || '执行失败');
        }
      } finally { hide(); }
    } catch (e) {
      message.error('执行失败');
    }
  };

  const openHistory = async (row) => {
    setHistoryTask(row);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/scheduled-tasks/${row.id}/runs`);
      setHistory(res.code === 200 ? (res.data.list || []) : []);
    } catch (e) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!editing) return;
    setPreviewing(true);
    try {
      const res = await api.get(`/scheduled-tasks/${editing.id}/preview`);
      if (res.code === 200) setPreview(res.data);
    } catch (e) {
      message.error('预估失败');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      const res = await api.delete(`/scheduled-tasks/${row.id}`);
      if (res.code === 200) { message.success('已删除'); loadList(); }
      else message.error(res.message || '删除失败');
    } catch (e) {
      message.error('删除失败');
    }
  };

  // 列表统一「两行式」排版：第一行业务值，第二行灰色补充说明，超过宽度省略号 + 悬浮显示全文
  const lineClamp = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const cell = (top, sub, { topStyle, subStyle, title } = {}) => (
    <Tooltip title={title || (typeof top === 'string' ? top : undefined)} mouseEnterDelay={0.4}>
      <div style={{ minWidth: 0 }}>
        <span style={{ ...lineClamp, ...topStyle }}>{top}</span>
        {sub !== undefined && sub !== null && sub !== '' && (
          <span style={{ ...lineClamp, fontSize: 12, color: '#8c8c8c', marginTop: 2, ...subStyle }}>{sub}</span>
        )}
      </div>
    </Tooltip>
  );

  const columns = [
    {
      title: '任务',
      dataIndex: 'name',
      width: 210,
      render: (v, row) => {
        const typeLabel = (meta.types.find(t => t.key === row.type) || {}).label || row.type;
        return (
          <div style={{ minWidth: 0 }}>
            <span style={{ ...lineClamp, fontWeight: 600 }} title={v}>{v}</span>
            <span style={{ ...lineClamp, fontSize: 12, color: '#8c8c8c', marginTop: 2 }} title={typeLabel}>
              <Tag color="blue" style={{ margin: '0 4px 0 0', lineHeight: '16px', fontSize: 11, padding: '0 4px' }}>{typeLabel}</Tag>
              {row.created_by_name || '-'}
            </span>
          </div>
        );
      }
    },
    {
      title: '调度规则',
      dataIndex: 'schedule_type',
      width: 200,
      render: (v, row) => cell(
        scheduleText(row),
        `时段 ${String(row.window_start || '').slice(0, 5)}–${String(row.window_end || '').slice(0, 5)}`,
        { title: `${scheduleText(row)}（执行时段 ${String(row.window_start || '').slice(0, 5)}–${String(row.window_end || '').slice(0, 5)}）` }
      )
    },
    {
      title: '参数摘要',
      width: 230,
      render: (_, row) => {
        const t = paramsText(row, meta);
        return cell(
          `${t.statuses} · ${t.suppliers}`,
          `${t.max} 个/次 · 冷却 ${t.cooldown} 分钟`,
          { title: `${t.statuses} · ${t.suppliers}｜${t.max} 个/次 · 冷却 ${t.cooldown} 分钟` }
        );
      }
    },
    {
      title: '执行身份',
      dataIndex: 'run_as_name',
      width: 110,
      render: (v, row) => cell(v || row.created_by_name || '-')
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 70,
      align: 'center',
      render: (v, row) => <Switch size="small" checked={!!v} onChange={(c) => toggleEnabled(row, c)} />
    },
    {
      title: '下次 / 上次',
      width: 200,
      render: (_, row) => {
        const next = row.enabled
          ? (row.next_run_at ? dayjs(row.next_run_at).format('MM-DD HH:mm') : '待计算')
          : '已停用';
        const last = row.last_run_at
          ? `${dayjs(row.last_run_at).format('MM-DD HH:mm')} · ${row.last_result || '已执行'}`
          : (row.last_result || '尚未执行');
        return cell(
          next,
          last,
          { subStyle: row.fail_streak > 0 ? { color: '#cf1322' } : undefined, title: `下次：${next}\n上次：${last}` }
        );
      }
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Tooltip title="立即执行">
            <Button type="link" size="small" icon={<ThunderboltOutlined />} onClick={() => runNow(row)}>执行</Button>
          </Tooltip>
          <Button type="link" size="small" onClick={() => openEdit(row)}>编辑</Button>
          <Button type="link" size="small" onClick={() => openHistory(row)}>历史</Button>
          <Popconfirm title="删除后历史任务记录仍保留，确认删除？" onConfirm={() => handleDelete(row)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>定时任务</h2>
          <Text type="secondary" style={{ fontSize: 12 }}>
            到点自动执行，等同于人工在「涨粉监控」点一次「拉取粉丝数」：任务记录、粉丝快照、操作日志都会留痕
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadList}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建定时任务</Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="定时任务执行时会写粉丝快照并在「涨粉监控 → 任务管理」里生成一条任务（来源标记为「定时」），夜间时段不执行。"
      />

      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          placeholder="搜索任务名称"
          allowClear
          style={{ width: 220 }}
          onSearch={(v) => setFilters(f => ({ ...f, name: v }))}
        />
        <Select
          style={{ width: 140 }}
          value={filters.enabled}
          onChange={(v) => setFilters(f => ({ ...f, enabled: v }))}
          options={[{ value: '', label: '全部状态' }, { value: '1', label: '已启用' }, { value: '0', label: '已停用' }]}
        />
        <Text type="secondary">共 {list.length} 个任务</Text>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        size="middle"
        tableLayout="fixed"
        scroll={{ x: 1220 }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
      />

      <Drawer
        title={editing ? `编辑定时任务 · ${editing.name}` : '新建定时任务'}
        width={620}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>保存</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onValuesChange={(c) => { if (c.schedule_type) setScheduleType(c.schedule_type); }}>
          <Divider orientation="left" style={{ marginTop: 0 }}>基本信息</Divider>
          <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input placeholder="如：每日涨粉巡检" maxLength={100} />
          </Form.Item>
          <Form.Item name="type" label="任务类型" rules={[{ required: true }]}>
            <Select options={(meta.types || []).map(t => ({ value: t.key, label: t.label }))} disabled={!!editing} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Divider orientation="left">调度规则</Divider>
          <Form.Item name="schedule_type" label="执行方式" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="interval">按间隔</Radio.Button>
              <Radio.Button value="daily">每天定时</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {scheduleType === 'interval' ? (
            <Form.Item name="interval_minutes" label="间隔（分钟）" rules={[{ required: true, message: '请输入间隔' }]}>
              <InputNumber min={5} max={1440} style={{ width: 160 }} addonAfter="分钟" />
            </Form.Item>
          ) : (
            <Form.Item
              name="daily_times"
              label="每天执行时刻"
              rules={[{ required: true, message: '请选择或输入执行时刻' }]}
              extra="点选或直接输入 HH:mm 后回车，可填多个"
            >
              <Select
                mode="tags"
                placeholder="HH:mm，如 09:00"
                tokenSeparators={[',', ' ']}
                options={['08:00', '09:00', '12:00', '14:00', '18:00', '20:00'].map(t => ({ value: t, label: t }))}
              />
            </Form.Item>
          )}
          <Space size="large" align="start">
            <Form.Item name="window_start" label="执行时段开始">
              <TimePicker format="HH:mm" minuteStep={10} />
            </Form.Item>
            <Form.Item name="window_end" label="执行时段结束">
              <TimePicker format="HH:mm" minuteStep={10} />
            </Form.Item>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            时段外的执行点自动顺延到下一个开始时刻，不补跑错过的时刻
          </Text>

          <Divider orientation="left">任务参数 · 拉取粉丝数</Divider>
          <Form.Item name="status_ids" label="目标状态" rules={[{ required: true, message: '请选择目标状态' }]}>
            <Select
              mode="multiple"
              placeholder="默认涨粉中"
              options={(meta.statuses || []).map(s => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Form.Item name="supplier_ids" label="主体范围">
            <Select
              mode="multiple"
              allowClear
              placeholder="不选 = 全部主体"
              options={(meta.suppliers || []).map(s => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Space size="large" align="start">
            <Form.Item
              name="max_orders"
              label="单次上限"
              extra="上限 5000（与手动拉取一致）；5000 个约需 2~3 小时，建议配合同号冷却"
            >
              <InputNumber min={1} max={5000} addonAfter="个" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="cooldown_minutes" label="同号冷却">
              <InputNumber min={0} max={10080} addonAfter="分钟" style={{ width: 170 }} />
            </Form.Item>
          </Space>
          <Form.Item name="order_by" label="取单顺序">
            <Select options={ORDER_BY_OPTIONS} />
          </Form.Item>
          {editing && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button size="small" loading={previewing} onClick={handlePreview}>预估待拉取数量</Button>
              {preview && (
                <Alert type="success" showIcon message={`预计待拉取 ${preview.count} 个抖音号，约 ${preview.estimate_minutes} 分钟`} />
              )}
            </Space>
          )}

          <Divider orientation="left">运行设置</Divider>
          <Form.Item name="run_as_user_id" label="执行身份">
            <Select
              allowClear
              placeholder="默认创建人"
              options={(meta.admins || []).map(u => ({ value: u.id, label: `${u.name}（${u.username}）` }))}
            />
          </Form.Item>
          <Form.Item name="fail_stop_after" label="连续失败自动停用（0 = 不停用）">
            <InputNumber min={0} max={20} addonAfter="次" style={{ width: 150 }} />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={historyTask ? `运行历史 · ${historyTask.name}` : '运行历史'}
        width={680}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      >
        <Spin spinning={historyLoading}>
          <Table
            rowKey="id"
            size="small"
            dataSource={history}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            columns={[
              { title: '任务号', dataIndex: 'id', width: 200, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
              { title: '开始', dataIndex: 'started_at', width: 130, render: v => (v ? dayjs(v).format('MM-DD HH:mm:ss') : '-') },
              {
                title: '结果', width: 140,
                render: (_, r) => `成功 ${r.success} / 失败 ${r.failed}（共 ${r.total}）`
              },
              {
                title: '状态', dataIndex: 'status', width: 90,
                render: v => {
                  const map = { done: ['green', '已完成'], running: ['blue', '执行中'], error: ['red', '已中止'], paused: ['orange', '已暂停'], cancelled: ['default', '已终止'] };
                  const [color, label] = map[v] || ['default', v];
                  return <Tag color={color}>{label}</Tag>;
                }
              },
              { title: '说明', dataIndex: 'message', render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v || '-'}</Text> }
            ]}
          />
        </Spin>
        <Text type="secondary" style={{ fontSize: 12 }}>
          完整逐号明细在「涨粉监控 → 任务管理」里查看，本任务的记录会标记为「定时」。
        </Text>
      </Drawer>
    </div>
  );
}
